// SUP offshore drift (review SUP-1, owner decision 7): the one definition of the rule, its sector
// and its two threshold sets (Touring and the legacy SUP; SUP Surf).
//
// The coastal page briefing (local-briefing.ts, P2C-WP9-04) runs in a client component, so it
// cannot import sup-scoring.ts or sup-profile-safety.ts, which use this rule for the engines and
// the named transport: together they are about 86 KB of source that would ship on every coastal
// page. The rule therefore lives in this dependency-free module, in the pattern of
// offshore-sector.ts (WFD-7), and those two modules re-export it from here, so there is no copy
// to drift. `sup-offshore-drift.test.ts` keeps it dependency-free and out of their weight.
//
// Pure: no imports, I/O, environment, clock or randomness.

/** SUP-1: offshore drift is assessed within this many degrees of dead offshore. */
export const SUP_OFFSHORE_DRIFT_SECTOR_DEG = 60;

export type SupOffshoreDriftThresholds = Readonly<{
  amberSustainedKmh: number;
  amberGustKmh: number;
  redSustainedKmh: number;
  redGustKmh: number;
}>;

/**
 * SUP-1 thresholds (owner decision 7; Touring, the legacy SUP and, through SUP-2(a), Downwind's
 * offshore displacement): amber from 12 km/h sustained or 20 km/h gusts, red from 20 km/h sustained
 * or 30 km/h gusts.
 *
 * The accelerated rollout Phase C preparation raised the red gust to 37 km/h on the reading that
 * Open-Meteo's land-cell gust (about 1.9 times the sustained wind at 15 to 25 km/h) is inflated and
 * its sustained wind is the wind on the water, so a 30 km/h gust "came with about 16 km/h
 * sustained, below the decision's own sustained red". That reading was withdrawn after the review
 * of the integrated Phase D tree (1 October 2026, gates addendum A14): against the Irish weather
 * buoys (Marine Institute M2 to M5, 9,869 hours paired with eleven Irish and Welsh spots) the land
 * cell's sustained wind is the damped one, 0.61 of the open-sea mean wind in the spots' offshore
 * sector, and in the offshore hours the 37 km/h gust took out of red (a modelled gust of 30 to 37
 * km/h on a sustained wind below 20) the open-sea mean wind reached 20 km/h in 83% of them (423
 * hours, median 28.7 km/h). The buoys are open sea and windier than the first kilometre off a beach
 * even when neither leg fires, so they cannot set a new threshold; they only show the gust leg
 * catching real over-water wind. Owner decision 7's 30 km/h stands until an observation-based
 * calibration of offshore flow near the beach exists.
 */
export const SUP_TOURING_OFFSHORE_DRIFT_THRESHOLDS: SupOffshoreDriftThresholds = Object.freeze({
  amberSustainedKmh: 12,
  amberGustKmh: 20,
  redSustainedKmh: 20,
  redGustKmh: 30,
});

/**
 * SUP Surf offshore-drift thresholds (policy v2), its approved revision 1 values: amber from 15
 * km/h sustained or 25 km/h gusts, red from 30 km/h sustained or 40 km/h gusts.
 *
 * The accelerated rollout Phase D lane raised the red gust to 50 km/h on the same reading as SUP-1's
 * 37 km/h (a 40 km/h land-cell gust "came with about 21 to 24 km/h sustained"), withdrawn after
 * review the same day (gates addendum A14): in the offshore hours a 50 km/h red would have taken out
 * of red (a modelled gust of 40 to 50 km/h on a sustained wind below 30) the open-sea mean wind at
 * the Irish buoys reached SUP Surf's own 30 km/h sustained red in 78% of them (524 hours, median
 * 35.2 km/h).
 */
export const SUP_SURF_OFFSHORE_DRIFT_THRESHOLDS: SupOffshoreDriftThresholds = Object.freeze({
  amberSustainedKmh: 15,
  amberGustKmh: 25,
  redSustainedKmh: 30,
  redGustKmh: 40,
});

export type SupOffshoreDriftInput = {
  windSpeedKmh: number | null;
  windGustKmh: number | null;
  windDirectionDeg: number | null;
  beachFacingDeg: number | null;
};

export type SupOffshoreDriftAssessment =
  | Readonly<{ level: "clear" | "amber" | "red" }>
  | Readonly<{ level: "unavailable"; provenance: "missing-local-geometry" | "missing-forecast-input" }>;

function finiteNonNegativeOrNull(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

function bearingOrNull(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) ? ((value % 360) + 360) % 360 : null;
}

/** Degrees between the wind's direction and dead offshore (0 = dead offshore, 180 = dead onshore). */
export function supAngleFromOffshoreDeg(windDirectionDeg: number, beachFacingDeg: number): number {
  const offshoreFrom = (beachFacingDeg + 180) % 360;
  const difference = Math.abs(windDirectionDeg - offshoreFrom) % 360;
  return Math.min(difference, 360 - difference);
}

/**
 * The offshore-drift level of one reading under the given thresholds (policy v2). A gust below
 * the sustained speed is inconsistent and ignored; a missing gust is bounded below by the
 * sustained speed and a missing sustained speed above by the gust. Within the offshore sector the
 * result is the lowest level the known readings make certain, and never clear while a hazard is
 * possible:
 * - red when red is certain;
 * - amber when amber is certain, even if a missing reading could make it red (a missing sustained
 *   wind with a 25 km/h gust is certainly amber and possibly red): the known hazard is reported as
 *   a floor, because an unavailable row would rank below it (day safety orders clear, unavailable,
 *   amber, red) and so hide it;
 * - unavailable when amber is possible but not certain (a missing reading could hide it), or when
 *   the geometry or the wind direction is missing and the wind could reach amber;
 * - clear only when the wind is known to be below amber, or known to blow outside the sector.
 */
export function assessSupOffshoreDrift(
  raw: SupOffshoreDriftInput,
  thresholds: SupOffshoreDriftThresholds = SUP_TOURING_OFFSHORE_DRIFT_THRESHOLDS,
): SupOffshoreDriftAssessment {
  const wind = finiteNonNegativeOrNull(raw.windSpeedKmh);
  const rawGust = finiteNonNegativeOrNull(raw.windGustKmh);
  const gust = rawGust !== null && (wind === null || rawGust >= wind) ? rawGust : null;
  // Lower bounds (certain) and upper bounds (possible) of each reading.
  const gustLow = gust ?? wind;
  const windHigh = wind ?? gust;
  const knownRed = (wind !== null && wind >= thresholds.redSustainedKmh) ||
    (gustLow !== null && gustLow >= thresholds.redGustKmh);
  const knownAmber = knownRed || (wind !== null && wind >= thresholds.amberSustainedKmh) ||
    (gustLow !== null && gustLow >= thresholds.amberGustKmh);
  const amberPossible = knownAmber ||
    windHigh === null || windHigh >= thresholds.amberSustainedKmh ||
    gust === null || gust >= thresholds.amberGustKmh;
  if (!amberPossible) return { level: "clear" };
  const facing = bearingOrNull(raw.beachFacingDeg);
  if (facing === null) return { level: "unavailable", provenance: "missing-local-geometry" };
  const direction = bearingOrNull(raw.windDirectionDeg);
  if (direction === null) return { level: "unavailable", provenance: "missing-forecast-input" };
  if (supAngleFromOffshoreDeg(direction, facing) > SUP_OFFSHORE_DRIFT_SECTOR_DEG) return { level: "clear" };
  if (knownRed) return { level: "red" };
  if (knownAmber) return { level: "amber" };
  return { level: "unavailable", provenance: "missing-forecast-input" };
}
